/**
 * The permission catalog, mirrored from `server/src/config/permissions.ts`.
 *
 * Hand-mirrored for `types.ts`'s reason, and held here rather than read from
 * `GET /permissions` because the panel needs the labels to draw a nav rail and a
 * role form before any request has answered. The server is the authority on what
 * a permission *does*; this file only knows what to call it. A key the server
 * adds and this file lacks still works — it is shown by its key, in "Other".
 *
 * Keys are `area.action`, one per thing an operator can do, so a role can say
 * "may acknowledge alerts but not resolve them" — which is the whole reason the
 * old four scopes were replaced.
 */

export const PERMISSIONS = [
  "alerts.view",
  "alerts.acknowledge",
  "alerts.resolve",
  "apps.view",
  "apps.create",
  "apps.update",
  "apps.manage_keys",
  "audit.view",
  "charge_points.view",
  "charge_points.register",
  "charge_points.rotate_password",
  "charge_points.update_sale",
  "charge_points.command",
  "charge_points.update_heartbeat",
  "charge_points.hide",
  "charge_points.delete",
  "connections.view",
  "customers.view",
  "customers.disable",
  "customers.enable",
  "depots.view",
  "depots.create",
  "depots.update",
  "events.view",
  "identities.view",
  "identities.create",
  "identities.update",
  "identities.delete",
  "identities.assign_allowance",
  "identities.remove_allowance",
  "logs.view",
  "operators.view",
  "operators.invite",
  "operators.update",
  "operators.assign_roles",
  "operators.disable",
  "operators.enable",
  "operators.issue_link",
  "payment_providers.view",
  "payment_providers.manage",
  "policies.view",
  "policies.create",
  "public_orders.view",
  "roles.view",
  "roles.create",
  "roles.update",
  "roles.delete",
  "sessions.view",
  "settings.view",
  "settings.update_payment_flow",
  "tariffs.view",
  "tariffs.create",
  "tariffs.update",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export interface PermissionInfo {
  readonly key: string;
  readonly group: string;
  readonly label: string;
  readonly description: string;
}

/** Area prefix → the heading a role form shows it under, in display order. */
const GROUP_LABELS: Readonly<Record<string, string>> = {
  charge_points: "Charge points",
  depots: "Depots",
  sessions: "Sessions",
  public_orders: "Public charging",
  customers: "Customers",
  tariffs: "Prices",
  identities: "Identities",
  policies: "Policies",
  alerts: "Alerts",
  connections: "Connections",
  events: "Events",
  audit: "Audit log",
  logs: "Server logs",
  settings: "Settings",
  operators: "Operators",
  roles: "Roles",
  payment_providers: "Payment providers",
  apps: "Apps",
};

const OTHER_GROUP = "Other";

const DETAILS: Readonly<Record<Permission, { label: string; description: string }>> = {
  "alerts.view": { label: "View alerts", description: "See open and resolved alerts." },
  "apps.view": {
    label: "View apps",
    description: "See third-party apps, their keys, what they may read, their limits and usage.",
  },
  "apps.create": { label: "Create apps", description: "Add a third-party app." },
  "apps.update": {
    label: "Edit apps",
    description: "Rename or disable an app, and change what it may read and how often.",
  },
  "apps.manage_keys": {
    label: "Manage app keys",
    description: "Issue an app's API keys and revoke them.",
  },
  "alerts.acknowledge": {
    label: "Acknowledge alerts",
    description: "Mark an alert as seen without closing it.",
  },
  "alerts.resolve": { label: "Resolve alerts", description: "Close an alert with a resolution." },
  "audit.view": {
    label: "View the audit log",
    description: "Read what every operator and token has done.",
  },
  "logs.view": {
    label: "View server logs",
    description: "Read what the server logged, including addresses, emails and payment detail.",
  },
  "charge_points.view": {
    label: "View charge points",
    description: "See stations, their frames, and the deployment's OCPP address.",
  },
  "charge_points.register": {
    label: "Register stations",
    description: "Add a station and mint the password it connects with.",
  },
  "charge_points.rotate_password": {
    label: "Rotate station passwords",
    description: "Replace a station's credential; the old one stops working.",
  },
  "charge_points.update_sale": {
    label: "Change public sale",
    description: "Rename a station, put it on public sale, or change its price.",
  },
  "charge_points.command": {
    label: "Send station commands",
    description: "Reset, unlock, start or stop charging remotely.",
  },
  "charge_points.update_heartbeat": {
    label: "Change heartbeat interval",
    description: "Set how often a station checks in; pushed to it at once if it is connected.",
  },
  "charge_points.hide": {
    label: "Hide stations",
    description:
      "Retire a station that has charged before: off the fleet and public sale, and refused if it connects.",
  },
  "charge_points.delete": {
    label: "Delete stations",
    description: "Remove a station that has never charged, with its password and message log.",
  },
  "connections.view": {
    label: "View connection attempts",
    description: "See which stations tried to connect, and why any were refused.",
  },
  "customers.view": { label: "View customers", description: "See driver accounts." },
  "customers.disable": {
    label: "Disable customers",
    description: "Stop a driver account and sign out its devices.",
  },
  "customers.enable": {
    label: "Enable customers",
    description: "Restore a disabled driver account.",
  },
  "depots.view": { label: "View depots", description: "See sites and their usage." },
  "depots.create": { label: "Create depots", description: "Add a new site." },
  "depots.update": {
    label: "Edit depots",
    description: "Rename a site, or stop it enrolling unseen cards.",
  },
  "events.view": { label: "View events", description: "Read the allowance event feed." },
  "identities.view": {
    label: "View cards",
    description: "See cards, holders and their pooled usage.",
  },
  "identities.create": {
    label: "Register cards",
    description: "Register a card or VIN, or create a holder.",
  },
  "identities.update": {
    label: "Edit cards",
    description: "Rename or move a card, and rename or move a holder.",
  },
  "identities.delete": {
    label: "Remove cards",
    description: "Delete a card that never charged, or an empty holder.",
  },
  "identities.assign_allowance": {
    label: "Assign allowances",
    description: "Set a policy on a holder, or change it.",
  },
  "identities.remove_allowance": {
    label: "Remove allowances",
    description: "Clear a holder's own policy.",
  },
  "operators.view": { label: "View operators", description: "See who can sign in to this panel." },
  "operators.invite": {
    label: "Invite operators",
    description: "Create an account and issue its invite link.",
  },
  "operators.update": { label: "Rename operators", description: "Change an operator's name." },
  "operators.assign_roles": {
    label: "Change operator access",
    description: "Give or take an operator's roles and direct permissions.",
  },
  "operators.disable": {
    label: "Disable operators",
    description: "Sign an operator out everywhere and stop them signing in.",
  },
  "operators.enable": { label: "Enable operators", description: "Restore a disabled operator." },
  "operators.issue_link": {
    label: "Issue sign-in links",
    description: "Send a new invite or a password reset link.",
  },
  "policies.view": { label: "View policies", description: "See allowance policies." },
  "policies.create": { label: "Create policies", description: "Add an allowance policy." },
  "public_orders.view": {
    label: "View public orders",
    description: "See public charge orders and the refund queue.",
  },
  "payment_providers.view": {
    label: "View payment providers",
    description:
      "Merchant accounts and which depots each serves. Never the credentials themselves.",
  },
  "payment_providers.manage": {
    label: "Manage payment providers",
    description:
      "Add a merchant account, replace its keys, choose the default, and move a depot to another one.",
  },
  "roles.view": { label: "View roles", description: "See roles and what each one allows." },
  "roles.create": { label: "Create roles", description: "Add a role." },
  "roles.update": { label: "Edit roles", description: "Change a role's name or permissions." },
  "roles.delete": {
    label: "Delete roles",
    description: "Remove a role from everyone who holds it.",
  },
  "sessions.view": { label: "View sessions", description: "See charging sessions." },
  "settings.view": { label: "View settings", description: "See deployment settings." },
  "settings.update_payment_flow": {
    label: "Change the payment flow",
    description: "Switch between charging the driver and holding the money.",
  },
  "tariffs.view": { label: "View prices", description: "See public prices." },
  "tariffs.create": { label: "Create prices", description: "Add a price." },
  "tariffs.update": { label: "Change prices", description: "Mint a new version of a price." },
};

export function isPermission(value: string): value is Permission {
  return (PERMISSIONS as readonly string[]).includes(value);
}

/** Label and group for any key — including one this build does not know. */
export function permissionInfo(key: string): PermissionInfo {
  const area = key.split(".")[0] ?? "";
  const known = isPermission(key) ? DETAILS[key] : undefined;
  return {
    key,
    group: GROUP_LABELS[area] ?? OTHER_GROUP,
    label: known?.label ?? key,
    description: known?.description ?? "",
  };
}

export interface PermissionGroup {
  readonly group: string;
  readonly permissions: readonly PermissionInfo[];
}

/**
 * Keys grouped by area, groups in the nav's order and keys in catalog order
 * within each. Unknown keys sort into a trailing "Other" group rather than being
 * dropped, so a newer server's permission is still visible and grantable.
 */
export function groupPermissions(keys: readonly string[]): readonly PermissionGroup[] {
  const order = [...Object.values(GROUP_LABELS), OTHER_GROUP];
  const rank = (key: string) => {
    const index = (PERMISSIONS as readonly string[]).indexOf(key);
    return index === -1 ? PERMISSIONS.length : index;
  };

  const byGroup = new Map<string, PermissionInfo[]>();
  for (const key of new Set(keys)) {
    const info = permissionInfo(key);
    byGroup.set(info.group, [...(byGroup.get(info.group) ?? []), info]);
  }

  return order
    .filter((group) => byGroup.has(group))
    .map((group) => ({
      group,
      permissions: [...(byGroup.get(group) ?? [])].sort(
        (a, b) => rank(a.key) - rank(b.key) || a.key.localeCompare(b.key),
      ),
    }));
}

/** Every key in the catalog, grouped — what a role form offers. */
export function catalogGroups(): readonly PermissionGroup[] {
  return groupPermissions(PERMISSIONS);
}

/**
 * The no-escalation rule, as the server enforces it: an actor may grant only what
 * they hold. The form disables the rest rather than hiding it, so a role's full
 * shape stays visible to someone who may edit only part of it.
 */
export function canGrant(held: readonly string[], key: string): boolean {
  return held.includes(key);
}

/** The keys in `wanted` the actor could not grant — empty means the change is allowed. */
export function ungrantable(held: readonly string[], wanted: readonly string[]): readonly string[] {
  return wanted.filter((key) => !canGrant(held, key));
}

/** Adds or removes one key, returning a new list in catalog order. */
export function togglePermission(current: readonly string[], key: string): readonly string[] {
  const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
  return sortPermissions(next);
}

/**
 * The group's "select all" toggle. Only keys the actor may grant are touched:
 * turning a group on adds those, turning it off removes those, and anything else
 * already on the role stays exactly as it was.
 */
export function toggleGroup(
  current: readonly string[],
  groupKeys: readonly string[],
  held: readonly string[],
): readonly string[] {
  const grantable = groupKeys.filter((key) => canGrant(held, key));
  const allOn = grantable.length > 0 && grantable.every((key) => current.includes(key));
  const next = allOn
    ? current.filter((key) => !grantable.includes(key))
    : [...new Set([...current, ...grantable])];
  return sortPermissions(next);
}

export function sortPermissions(keys: readonly string[]): readonly string[] {
  const rank = (key: string) => {
    const index = (PERMISSIONS as readonly string[]).indexOf(key);
    return index === -1 ? PERMISSIONS.length : index;
  };
  return [...new Set(keys)].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

/** "12 of 42", for a role row or an operator's effective access. */
export function permissionCount(keys: readonly string[]): string {
  return `${new Set(keys).size} of ${PERMISSIONS.length}`;
}
